import { DashboardLayoutItem } from "@/modules/dashboards/pages/DashboardTypes";

export const GRID_COLUMNS = 12;

// Displacement only begins once the dragged card covers at least this
// fraction of the smaller of the two cards' areas.
export const DISPLACEMENT_OVERLAP_RATIO = 0.5;

export type GridRect = { x: number; y: number; w: number; h: number };
export type GridPosition = { x: number; y: number };
export type DragSnapshot = Map<number | string, GridPosition>;

type DisplacementAxis = "x" | "y";
type DisplacementDirection = -1 | 1;

export const takeDragSnapshot = (cards: DashboardLayoutItem[]): DragSnapshot =>
	new Map(cards.map((card) => [card.i, { x: card.x, y: card.y }]));

export const rectanglesOverlap = (a: GridRect, b: GridRect) =>
	a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const overlapArea = (a: GridRect, b: GridRect) => {
	const overlapW = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
	const overlapH = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
	return overlapW > 0 && overlapH > 0 ? overlapW * overlapH : 0;
};

const minimalShove = (
	card: GridRect,
	draggedRect: GridRect,
	axis: DisplacementAxis,
	direction: DisplacementDirection
): GridRect | null => {
	if (axis === "x") {
		const x =
			direction === -1
				? draggedRect.x - card.w
				: draggedRect.x + draggedRect.w;
		return x >= 0 && x + card.w <= GRID_COLUMNS
			? { x, y: card.y, w: card.w, h: card.h }
			: null;
	}
	const y =
		direction === -1 ? draggedRect.y - card.h : draggedRect.y + draggedRect.h;
	return y >= 0 ? { x: card.x, y, w: card.w, h: card.h } : null;
};

const findNearestFreeSpot = (
	card: GridRect,
	home: GridPosition,
	obstacles: GridRect[],
	axis: DisplacementAxis,
	direction: DisplacementDirection
): GridRect => {
	// The row band below every obstacle is always free, so a spot always exists.
	const maxY = Math.max(...obstacles.map((obstacle) => obstacle.y + obstacle.h));
	let best: GridRect | null = null;
	let bestDistance = Infinity;
	let bestPush = -Infinity;
	for (let y = 0; y <= maxY; y++) {
		for (let x = 0; x <= GRID_COLUMNS - card.w; x++) {
			const distance = Math.abs(x - home.x) + Math.abs(y - home.y);
			const push = (axis === "x" ? x - home.x : y - home.y) * direction;
			if (
				distance > bestDistance ||
				(distance === bestDistance && push <= bestPush)
			) {
				continue;
			}
			const candidate = { x, y, w: card.w, h: card.h };
			if (obstacles.some((obstacle) => rectanglesOverlap(candidate, obstacle))) {
				continue;
			}
			best = candidate;
			bestDistance = distance;
			bestPush = push;
		}
	}
	return best ?? { x: home.x, y: home.y, w: card.w, h: card.h };
};

/**
 * Treats the dragged card as a solid object: every card it overlaps is shoved
 * along the dominant drag axis toward the space the dragged card vacated, just
 * far enough to clear it. Cards that can't be shoved (grid edge or another
 * card in the way) go to the nearest free spot relative to their position at
 * drag start. All non-dragged cards are first reset to their snapshot
 * positions, so the drag acts as a pure preview until it is committed.
 *
 * Displacement only begins once some overlapped card crosses
 * DISPLACEMENT_OVERLAP_RATIO; below that, overlapped cards stay put.
 *
 * Returns whether the dragged card can settle at draggedTo: true when nothing
 * overlaps it there (either no card did, or displacement cleared them), false
 * while overlaps remain below the threshold.
 */
export const displaceCardsOnDrag = (
	cards: DashboardLayoutItem[],
	draggedCardId: number | string,
	draggedTo: GridPosition,
	snapshot: DragSnapshot
): boolean => {
	const draggedCard = cards.find((card) => card.i === draggedCardId);
	if (!draggedCard) return false;

	const otherCards = cards.filter((card) => card.i !== draggedCardId);
	for (const card of otherCards) {
		const home = snapshot.get(card.i);
		if (home) {
			card.x = home.x;
			card.y = home.y;
		}
	}

	const draggedRect: GridRect = {
		x: draggedTo.x,
		y: draggedTo.y,
		w: draggedCard.w,
		h: draggedCard.h,
	};
	const draggedHome = snapshot.get(draggedCardId) ?? {
		x: draggedCard.x,
		y: draggedCard.y,
	};
	const dragX = draggedTo.x - draggedHome.x;
	const dragY = draggedTo.y - draggedHome.y;
	const axis: DisplacementAxis = Math.abs(dragY) > Math.abs(dragX) ? "y" : "x";
	const direction: DisplacementDirection =
		(axis === "x" ? dragX : dragY) > 0 ? -1 : 1;

	const displacedCards = otherCards
		.filter((card) => rectanglesOverlap(draggedRect, card))
		.sort((a, b) => overlapArea(draggedRect, b) - overlapArea(draggedRect, a));

	const draggedArea = draggedRect.w * draggedRect.h;
	const thresholdReached = displacedCards.some(
		(card) =>
			overlapArea(draggedRect, card) >=
			Math.min(card.w * card.h, draggedArea) * DISPLACEMENT_OVERLAP_RATIO
	);
	if (!thresholdReached) {
		return displacedCards.length === 0;
	}

	const displacedIds = new Set(displacedCards.map((card) => card.i));

	const obstacles: GridRect[] = [
		draggedRect,
		...otherCards.filter((card) => !displacedIds.has(card.i)),
	];

	for (const card of displacedCards) {
		const shoved = minimalShove(card, draggedRect, axis, direction);
		const target =
			shoved &&
			!obstacles.some((obstacle) => rectanglesOverlap(shoved, obstacle))
				? shoved
				: findNearestFreeSpot(
						card,
						snapshot.get(card.i) ?? card,
						obstacles,
						axis,
						direction
				  );
		card.x = target.x;
		card.y = target.y;
		obstacles.push(target);
	}

	return true;
};

/**
 * Commits a drag. When the drop position settles (no overlap, or displacement
 * cleared it), the dragged card lands there. When the overlap stayed below the
 * threshold, the card lands where the drag placeholder showed it: its last
 * accepted grid position — validated, because restoring previously displaced
 * cards may have reoccupied that spot, in which case the nearest free spot is
 * used instead.
 */
export const settleCardAfterDrop = (
	cards: DashboardLayoutItem[],
	draggedCardId: number | string,
	droppedAt: GridPosition,
	snapshot: DragSnapshot
): void => {
	const draggedCard = cards.find((card) => card.i === draggedCardId);
	if (!draggedCard) return;

	if (displaceCardsOnDrag(cards, draggedCardId, droppedAt, snapshot)) {
		draggedCard.x = droppedAt.x;
		draggedCard.y = droppedAt.y;
		return;
	}

	const otherCards = cards.filter((card) => card.i !== draggedCardId);
	const placeholderRect: GridRect = {
		x: draggedCard.x,
		y: draggedCard.y,
		w: draggedCard.w,
		h: draggedCard.h,
	};
	if (!otherCards.some((card) => rectanglesOverlap(placeholderRect, card))) {
		return;
	}

	const draggedHome = snapshot.get(draggedCardId) ?? placeholderRect;
	const dragX = droppedAt.x - draggedHome.x;
	const dragY = droppedAt.y - draggedHome.y;
	const axis: DisplacementAxis = Math.abs(dragY) > Math.abs(dragX) ? "y" : "x";
	const direction: DisplacementDirection =
		(axis === "x" ? dragX : dragY) > 0 ? -1 : 1;

	const target = findNearestFreeSpot(
		placeholderRect,
		{ x: placeholderRect.x, y: placeholderRect.y },
		otherCards,
		axis,
		direction
	);
	draggedCard.x = target.x;
	draggedCard.y = target.y;
};